// @ts-nocheck
import { get } from 'widgets/toolbox/util';

const keyCode = Object.freeze({
    RETURN: 13,
    SPACE: 32
});

/**
 * @typedef {ReturnType<typeof import('widgets/forms/AddressForm').default>} AddressForm
 * @typedef {InstanceType<ReturnType<typeof import('widgets/forms/BasicInput').default>>} basicInput
 * @typedef {InstanceType<ReturnType<typeof import('widgets/forms/AutocompleteField').default>>} autocompleteField
 * @typedef {InstanceType<ReturnType<typeof import('widgets/checkout/RadioSelector').default>>} radioSelector
 * @typedef {InstanceType<typeof import('widgets/toolbox/RefElement').RefElement>} refElement
 *
 */

/**
 * @param {AddressForm} AddressForm Base widget for extending
 * @returns {typeof AddressCheckoutForm} Address Checkout Form widget
 */
export default function (AddressForm) {
    /**
     * @category widgets
     * @subcategory checkout
     * @class AddressCheckoutForm
     * @augments AddressForm
     * @property {string} data-address-selector - Saved addresses selector
     * @property {string} data-address-fields - List of address fields
     * @classdesc Checkout address form component, that has common functionality for billing ans shipping address.
     * <br> It with next features:
     * 1. Handles `edit` button click
     * 2. Handles `add new address` button click
     * 3. Handles return from new to saved addresses
     * 4. Handles saved address changing
     * 5. Filters similar addresses for address selector
     * 6. Validates form before submission
     *
     * * Widget has next relationship:
     * * {@link RadioSelector#hide}/{@link RadioSelector#show} is used to toggle saved addresses
     * * {@link RadioSelector#getDataSet} is used to get selected address properties
     * * {@link RadioSelector#getDataValue} is used to get selected address
     * * {@link RadioSelector#updateState} is used to rerender saved addresses list
     * * Also {@link basicInput} methods are used to fill/clear/validate form fields
     * @example <caption>Checkout shipping form</caption>
     * <form
     *     autocomplete="on"
     *     novalidate
     *     data-widget="shippingForm"
     *     data-event-submit.prevent="handleSubmit"
     *     data-widget-event-updateshippingform="updateSummaryEv"
     *     data-address-selector="shippingAddressSelector"
     *     data-address-fields="${[
     *         shippingFields.firstName.htmlName,
     *         shippingFields.lastName.htmlName,
     *         shippingFields.address1.htmlName,
     *         shippingFields.address2.htmlName,
     *         shippingFields.country.htmlName,
     *         shippingFields.states.stateCode.htmlName,
     *         shippingFields.city.htmlName,
     *         shippingFields.postalCode.htmlName,
     *         shippingFields.phone.htmlName
     *     ].join(',')}"
     *     data-widget-event-submit="shippingSubmitted"
     *     action="${URLUtils.url('CheckoutShippingServices-SubmitShipping')}"
     *     data-form-definition-url="${URLUtils.url('Forms-FormDefinition', 'form', 'shipping')}"
     *     <isprint value=${pdict.forms.shippingForm.attributes} encoding="off"/>
     *     data-address-autocomplete-enabled="${!!require('util/sitepref').getValue('googleAutocompleteEnabled')}"
     * >
     *     ...
     *         <div
     *             class="b-form-message"
     *             role="alert"
     *             hidden="hidden"
     *             data-ref="errorMessageLabel"
     *         ></div>
     *     ...
     *     <isinclude template="checkout/shipping/shipmentSelector" />
     *     ...
     *     <isinclude template="checkout/shipping/shippingAddress" />
     *     ...
     * </form>
     *
     */
    class AddressCheckoutForm extends AddressForm {
        prefs() {
            return {
                addressPropertiesNames: [
                    'firstName',
                    'lastName',
                    'address1',
                    'address2',
                    'city',
                    'postalCode',
                    'stateCode',
                    'phone',
                    'countryCode.value'
                ],
                addressSelector: '',
                ...super.prefs()
            };
        }

        /**
         * @description Get address fields object, where:
         * * key - field short name like `firstName`
         * * value - {@link BasicInput} form field
         * @returns {object} result
         */
        getFormFieldsShort() {
            const fileds = this.getFormFields();

            return Object.keys(fileds).reduce((acc, fullKey) => {
                const shortKey = fullKey.split('_').pop();

                if (shortKey) {
                    acc[shortKey] = fileds[fullKey];
                }

                return acc;
            }, {});
        }

        /**
         * @description Hide form with address fields
         * @returns {void}
         */
        hideAddressFields() {
            this.ref('addressForm').hide();
            this.ref('addressForm').disable();
        }

        /**
         * @description Show form with address fields
         * @returns {void}
         */
        showAddressFields() {
            this.ref('addressForm').show();
            this.ref('addressForm').enable();
        }

        /**
         * @description Handle edit button click on the saved address
         * *set selected address
         * *copy address to the from
         * *show address form
         * @param {refElement} updateAddressBtn - Edit address button
         * @listens dom#click
         * @returns {void}
         */
        updateAddress(updateAddressBtn) {
            const addressID = updateAddressBtn && updateAddressBtn.data('uuid');

            if (addressID) {
                this.setSelectedAddress(addressID);
                this.updateAddressSelector(() => {
                    this.showAddressForm();
                    this.fulfillAddress();
                    this.callIfExists('focusFirstInput', 'addressForm'); // class AccessibilityFocusMixin, method focusFirstInput
                });
            }
        }

        /**
         * @description Keydown Event handler
         * @param {refElement} updateAddressBtn Source of keydown event
         * @param {KeyboardEvent} event  Event object
         * @returns {void}
         */
        handleKeydown(updateAddressBtn, event) {
            if (event.keyCode === keyCode.RETURN || event.keyCode === keyCode.SPACE) {
                event.stopPropagation();
                event.preventDefault();
                this.updateAddress(updateAddressBtn);
            }
        }

        /**
         * @description Show address form and hide saved addresses
         * @returns {void}
         */
        showAddressForm() {
            this.hideSavedAddresses();
            this.showAddressFields();
        }

        /**
         * @description Hide address form and show saved addresses
         * @returns {void}
         */
        hideAddressForm() {
            this.showSavedAddresses();
            this.hideAddressFields();
        }

        /**
         * @description Fill address fields based on datased
         * @param {object} dataset - Object with address data
         * @returns {void}
         */
        fillAddressFields(dataset) {
            this.getAddressFields().forEach(item => {
                this.getById(item, (/** @type {basicInput} */addressField) => {
                    const name = addressField.getName() || '';
                    const shortName = name.split('_').pop();

                    if (shortName && shortName !== 'country') {
                        addressField.setValue(dataset[shortName] || '', true);
                    } else {
                        addressField.setValue(dataset.countryCode || addressField.initValue || '', true);
                        this.config.countryCode = addressField.getValue();
                    }
                });
            });
            this.updateAddressAutocomplete();
        }

        /**
         * @description Update form definition if form definition not valid for saved address.
         * Then call method to fill saved address into address fields
         * @returns {void}
         */
        fulfillAddress() {
            this.getById(this.prefs().addressSelector, (/** @type {radioSelector} */addressSelector) => {
                const dataset = addressSelector.getDataSet();

                if (!dataset || (addressSelector.data('value') === 'new')) {
                    return;
                }

                const definitionReloaded = this.getAddressFields().some(item => {
                    let isReloaded = false;

                    this.getById(item, /** @type {basicInput} */addressField => {
                        const name = addressField.getName() || '';
                        const shortName = name.split('_').pop();

                        if (shortName === 'country') {
                            const countryCode = addressField.getValue();

                            if (dataset.countryCode && (countryCode !== dataset.countryCode)) {
                                this.updateFormData({ countryCode: dataset.countryCode }).then(() => {
                                    this.fillAddressFields(dataset);
                                });
                                isReloaded = true;
                            }
                        }
                    });

                    return isReloaded;
                });

                if (!definitionReloaded) {
                    this.fillAddressFields(dataset);
                    this.formDataUpdated();
                }
            });
        }

        /**
         * @description Handle click on add new address button.
         * Toggle saved and new address blocks
         * @listens dom#click
         * @returns {void}
         */
        addNewAddress() {
            this.getById(this.prefs().addressSelector, (/** @type {radioSelector} */addressSelector) => {
                addressSelector.data('value', 'new');
            });

            this.getAddressFields().forEach(item => {
                this.getById(item, (/** @type {basicInput} */addressField) => {
                    const name = addressField.getName() || '';

                    addressField.clearError();
                    if (!name.includes('country')) {
                        addressField.setValue('', true);
                    } else {
                        addressField.setValue(addressField.initValue);
                        this.config.countryCode = addressField.initValue;
                    }
                });
            });
            if (this.prefs().addressAutocompleteEnabled) {
                this.getById(
                    this.prefs().addressAutocompleteProvider,
                    (/** @type {autocompleteField} */autocompleteProviderField) => {
                        autocompleteProviderField.setValue('', true);
                    }
                );
            }

            this.showAddressForm();
            this.updateAddressAutocomplete();
            this.callIfExists('focusFirstInput', 'addressForm'); // class AccessibilityFocusMixin, method focusFirstInput
        }

        /**
         * @description Handle click on back to saved addresses button
         * @listens dom#click
         * @returns {void}
         */
        backToAddresses() {
            this.showSavedAddresses();
            this.changeAddress();
        }

        /**
         * @description Hide saved addresses block
         * @returns {void}
         */
        hideSavedAddresses() {
            this.getById(this.prefs().addressSelector, (/** @type {radioSelector} */addressSelector) => {
                addressSelector.hide();
            });
        }

        /**
         * @description Show saved addresses block
         * @returns {void}
         */
        showSavedAddresses() {
            /* eslint-disable */
            // Remove non US address from shipping page
            var defaultWineclubShipAdd = document.querySelectorAll("fieldset[data-id='shippingAddressSelector'] input[data-country-code]");
            if(defaultWineclubShipAdd !== null && defaultWineclubShipAdd.length) {
                for (var i = 0; i < defaultWineclubShipAdd.length; i++) {
                    var radioBtns = defaultWineclubShipAdd[i];
                    if(radioBtns.getAttribute('data-country-code') !== "US") {
                        /*eslint no-undef: "error"*/
                        radioBtns.closest('div.b-option_switch').remove(); // no error
                    }
                }
            }
            /* eslint-enable */
            this.getById(this.prefs().addressSelector, (/** @type {radioSelector} */addressSelector) => {
                addressSelector.show();
            });
        }

        /**
         * @description Handle saved address change.
         * Prefill saved address to the address form
         * @listens BasicInput#change
         * @returns {void}
         */
        changeAddress() {
            this.getById(this.prefs().addressSelector, (/** @type {radioSelector} */addressSelector) => {
                const addressID = addressSelector.getDataValue();

                this.setSelectedAddress(addressID);
                this.updateAddressSelector();
            });
        }

        /**
         * @description Checks if addresses are similar based on comparison of predefined fields
         * @param {object} address1 - Base address
         * @param {object} address2 - Verifiable address
         * @returns {boolean} Is similar addresses
         */
        isSimilarAddress(address1, address2) {
            return this.prefs().addressPropertiesNames.every(field => get(address1, field) === get(address2, field));
        }

        /**
         * @description Set selected address
         * @param {string} addressID - Selected address ID
         * @returns {void}
         */
        setSelectedAddress(addressID) {
            this.selectedAddressID = addressID;
        }

        /**
         * @description Set `selected` attribute for selected address
         * @param {object} addresses - Addresses object
         * @returns {void}
         */
        markAddressSelection(addresses = []) {
            addresses.forEach(address => {
                address.selected = address.addressUUID === this.selectedAddressID;
            });
        }

        /**
         * @description Return applicable addresses for current form
         * @param {object} [order] - order data
         * @param {object} customer - customer data
         * @returns {Array} returns customer addresses
         */
        getAddressOptions(order, customer) {
            return [...customer.addresses];
        }

        /**
         * @description Prepare address options for displaying
         * @param {object} order - order data
         * @param {object} customer - customer data
         * @returns {Array} returns prepared options
         */
        getPreparedAddressOptions(order, customer) {
            const addresses = this.getAddressOptions(order, customer);
            const filteredAddresses = this.filterAddresses(addresses);

            if (!this.selectedAddressID && filteredAddresses.length) {
                this.setSelectedAddress(filteredAddresses[0].addressUUID);
            }
            this.markAddressSelection(filteredAddresses);

            return filteredAddresses;
        }

        /**
         * @description Filter addresses
         * @param {object} addresses - Addresses for filtering
         * @returns {Array} Filtered addresses
         */
        filterAddresses(addresses) {
            return this.filterDuplicates(addresses);
        }

        /**
         * @description Filter duplicates in address array
         * @param {object} addresses - Addresses for filtering
         * @returns {Array} Addresses without duplicates
         */
        filterDuplicates(addresses) {
            const result = [];

            addresses.forEach((address) => {
                let isSameAddressAdded = false;

                for (let i = 0, l = result.length; i < l; i++) {
                    const resultAddress = result[i];

                    if (this.isSimilarAddress(resultAddress, address)) {
                        isSameAddressAdded = true;
                        if (address.isDefault) {
                            resultAddress.isDefault = true;
                        }
                        if (address.addressUUID === this.selectedAddressID) {
                            this.setSelectedAddress(resultAddress.addressUUID);
                        }
                        break;
                    }
                }
                if (!isSameAddressAdded) {
                    result.push(address);
                }
            });

            return result;
        }

        /**
         * @description Initialize saved addresses selector
         * @returns {Promise| undefined} Rendering promise or `undefined`
         */
        activateAddressSelector() {
            return this.getById(this.prefs().addressSelector, (/** @type {radioSelector} */addressSelector) => {
                return addressSelector.updateState({
                    addresses: this.getPreparedAddressOptions(this.order, this.customer),
                    isFieldsShown: false
                }).then(() => {
                    if (addressSelector.data('value') !== 'new') {
                        this.hideAddressForm();
                        this.fulfillAddress();
                    } else {
                        this.showAddressForm();
                    }

                    this.onAddressSelectorActivated();
                });
            });
        }

        /**
         * @description Rerender saved addresses list
         * @param {Function} [cb] - Callback to call after update
         * @returns {void}
         */
        updateAddressSelector(cb) {
            if (this.order && this.customer) {
                this.getById(this.prefs().addressSelector, (/** @type {radioSelector} */addressSelector) => {
                    addressSelector.updateState({
                        addresses: this.getPreparedAddressOptions(this.order, this.customer)
                    }).then(() => {
                        if (cb) {
                            cb();
                        } else {
                            this.fulfillAddress();
                            this.hideAddressForm();
                            addressSelector.focusInput();
                        }
                    });
                });
            }
        }

        /**
         * @description Address selector activated callback
         * @returns {void}
         */
        onAddressSelectorActivated() {
            this.callIfExists('focusFirstInput'); // class AccessibilityFocusMixin, method focusFirstInput
        }

        /**
         * @description Submit form handler
         * @returns {void}
         */
        submit() {
            if (this.isValid()) {
                this.handleSubmit();
            } else {
                this.showAddressForm();
                this.validate();
            }
        }

        /**
         * @description Update internal order object
         * @param {object} order - Order object
         * @returns {void}
         */
        updateOrder(order) {
            this.order = order;
        }

        /**
         * @description Update internal order object
         * @param {object} params - Object with necessary data for activation
         * @param {object} params.order - Order object
         * @param {object} params.customer - Customer object
         * @returns {void}
         */
        activate({ order, customer }) {
            this.order = order;
            this.customer = customer;
            this.activateAddressSelector();
        }
    }

    return AddressCheckoutForm;
}
